import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MessageSquare, Star, Send, CheckCircle2, Sparkles, Building2, Flame } from "lucide-react";
import { toast } from "sonner";

const INITIAL = {
  about: "ours",
  vendor_name: "",
  subject_type: "service",
  rating: 5,
  comment: "",
  person_name: "",
  person_phone: "",
  is_customer: false,
};

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [streak, setStreak] = useState({ today: 0, week: 0 });

  useEffect(() => {
    const load = () => api.get("/feedback/public/streak")
      .then(({ data }) => setStreak(data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [done]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.person_name || !form.comment) {
      toast.error("Name and comment are required.");
      return;
    }
    if (form.about === "external" && !form.vendor_name) {
      toast.error("Please name the vendor / brand you're rating.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/feedback/public", payload);
      setDone(true);
      setForm(INITIAL);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => { setOpen(v); if (!v) setDone(false); }}
    >
      <SheetTrigger asChild>
        <button
          aria-label="Share feedback"
          data-testid="feedback-widget-button"
          className="fixed bottom-24 right-4 z-40 flex items-center gap-2 rounded-full bg-brand-orange px-4 py-3 text-sm font-bold text-white shadow-2xl shadow-brand-orange/40 transition-transform hover:-translate-y-0.5 sm:bottom-6 sm:right-6"
        >
          <MessageSquare size={16} />
          <span className="hidden sm:inline">Feedback</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="feedback-widget-panel">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl font-extrabold">
            Share feedback
          </SheetTitle>
          <p className="text-xs text-muted-foreground">
            Rate us or any other vendor — it feeds our analytics dashboards directly.
          </p>
        </SheetHeader>

        {done ? (
          <div className="mt-6 rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-6 text-center" data-testid="widget-success">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-brand-orange text-white">
              <CheckCircle2 size={24} />
            </div>
            <div className="mt-3 font-heading text-lg font-bold">Thank you!</div>
            <p className="mt-1 text-xs text-muted-foreground">Your voice is now shaping our next release.</p>
            <div className="mt-4 flex justify-center gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setDone(false)}>Share more</Button>
              <Button className="rounded-full bg-brand-ink text-white" onClick={() => setOpen(false)}>Close</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-4" data-testid="widget-form">
            <div className="grid grid-cols-2 gap-1 rounded-xl bg-muted p-1">
              {[
                { v: "ours", label: "Us", icon: Sparkles },
                { v: "external", label: "Other vendor", icon: Building2 },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, about: o.v }))}
                  data-testid={`widget-about-${o.v}`}
                  className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
                    form.about === o.v ? "bg-white shadow" : "text-muted-foreground"
                  }`}
                >
                  <o.icon size={12} /> {o.label}
                </button>
              ))}
            </div>

            {form.about === "external" && (
              <Input
                value={form.vendor_name}
                onChange={set("vendor_name")}
                placeholder="Vendor / brand name"
                data-testid="widget-vendor"
              />
            )}

            <div>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Rating</div>
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    data-testid={`widget-star-${n}`}
                  >
                    <Star size={24} className={n <= form.rating ? "fill-brand-yellow text-brand-yellow" : "text-muted-foreground/50"} />
                  </button>
                ))}
              </div>
            </div>

            <Input value={form.person_name} onChange={set("person_name")} placeholder="Your name *" required data-testid="widget-name" />
            <Input value={form.person_phone} onChange={set("person_phone")} placeholder="Phone (optional)" data-testid="widget-phone" />
            <Textarea
              value={form.comment}
              onChange={set("comment")}
              placeholder="What worked or what didn't? *"
              rows={4}
              required
              data-testid="widget-comment"
            />

            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
              data-testid="widget-submit"
            >
              <Send size={14} className="mr-1.5" />
              {submitting ? "Submitting..." : "Submit feedback"}
            </Button>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
